import { useContext } from 'react';
import ServicesContext from '../../global/services/ServicesContext';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { mutateDocumentsKeys } from './mutateKeyFactory';
import { useSearchContext } from '../search/SearchContext';
import { searchKeys } from '../search/queryKeyFactory';
import { UpdateDocumentMetadata } from '../../Model/requests/UpdateDocumentMetadata';
import { GearDocumentMetadata } from '../../Model/GearDocumentMetadata';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { v4 as uuidv4 } from 'uuid';
import { GEAR_NOTIFICATION_TYPE } from '../../Model/enums/GearNotificationType';
import { useGetCurrentUser } from '../currentUser/useGetCurrentUser';
import { FormRetryData } from '../form/DocumentFormContext';
import { documentsKeys } from './queryKeyFactory';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';

export function useUpdateDocumentMetadata(): UseMutationResult<
  { metadata: GearDocumentMetadata[] },
  unknown,
  { dto: UpdateDocumentMetadata; etag?: string }
> {
  const servicesCtx = useContext(ServicesContext);
  const queryClient = useQueryClient();
  const { queryRequest } = useSearchContext();
  const { addCenterNotification, showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { data: currentUser } = useGetCurrentUser();

  const updateDocumentMetadata = (data: {
    dto: UpdateDocumentMetadata;
    etag?: string;
  }): Promise<{ metadata: GearDocumentMetadata[] }> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.updateDocumentMetadata(data.dto, data.etag);
    }
    throw new Error('Services context not initialized');
  };

  const handle412Conflict = (variables: { dto: UpdateDocumentMetadata; etag?: string }) => {
    showToast({
      type: GEAR_NOTIFICATION_TYPE.WARNING,
      message: translate('custom.notifications.notificationCenter.updateDocument.documentChangedRefreshRequired')
    });

    addCenterNotification({
      id: uuidv4(),
      title: translate('custom.notifications.notificationCenter.updateDocument.conflictTitle'),
      message: translate('custom.notifications.notificationCenter.updateDocument.conflictMessage', {
        fileName: variables.dto.fileName
      }),
      timestamp: new Date(),
      type: 'warning'
    });

    queryClient.invalidateQueries({
      queryKey: documentsKeys.documentMetadata({
        indexId: variables.dto.searchIndexId,
        ctName: variables.dto.ctName,
        documentId: variables.dto.documentId
      })
    });
  };

  const handleGenericError = (variables: { dto: UpdateDocumentMetadata; etag?: string }) => {
    const retryIndex = currentUser?.indexes.find(index => index.id === variables.dto.searchIndexId);

    if (retryIndex && variables.dto.metadata) {
      const retryData: FormRetryData = {
        formType: 'edit',
        documentId: variables.dto.documentId,
        metadata: variables.dto.metadata,
        contentType: variables.dto.ctName,
        retryIndex,
        fileUploadRequired: false
      };

      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.updateDocument.updateDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.updateDocument.updateDocumentFailedMessage', {
          fileName: variables.dto.fileName
        }),
        timestamp: new Date(),
        type: 'error',
        formRetryData: retryData
      });
    }
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.updateDocumentMetadata,
    mutationFn: (data: { dto: UpdateDocumentMetadata; etag?: string }) => updateDocumentMetadata(data),
    onSuccess: async (data, variables) => {
      showToast({
        type: GEAR_NOTIFICATION_TYPE.SUCCESS,
        message: translate('custom.toolbar.actions.form.docUpdatedSuccessfully')
      });

      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.updateDocument.updateDocumentSuccessTitle', {
          fileName: variables.dto.fileName
        }),
        message: translate('custom.notifications.notificationCenter.updateDocument.updateDocumentSuccessMessage'),
        timestamp: new Date(),
        type: 'success'
      });

      if (queryRequest) {
        await queryClient.invalidateQueries({ queryKey: searchKeys.filteredSearch(queryRequest) });
      }

      const getDocumentMetadataDto = {
        indexId: variables.dto.searchIndexId,
        ctName: variables.dto.ctName,
        documentId: variables.dto.documentId
      };
      await queryClient.invalidateQueries({ queryKey: documentsKeys.documentMetadata(getDocumentMetadataDto) });
    },
    onError: (error, variables) => {
      const status = (error as any)?.status || (error as any)?.response?.status;

      if (status === 412) {
        handle412Conflict(variables);
        return;
      }

      handleGenericError(variables);
    }
  });
}
